import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";
import { getDropStatus } from "../pages/creator/creatorHelpers";

export const FEED_DROP_LIMIT = 50;

function mapDropDocs(snap) {
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** True when drop should appear in Gallery / Home open feeds (handoff: open drops). */
export function isOpenFeedDrop(drop) {
  return getDropStatus(drop) === "open";
}

/**
 * Fetch up to `max` drops for Gallery / Home feeds.
 * Uses status == "open" when possible, then backfills with computed-open drops
 * so seed rows missing `status` are not excluded.
 */
export async function fetchFeedDrops(max = FEED_DROP_LIMIT) {
  const cap = Math.max(1, max);

  const [openSnap, allSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, "drops"),
        where("status", "==", "open"),
        limit(cap)
      )
    ).catch(() => ({ docs: [], size: 0 })),
    getDocs(query(collection(db, "drops"), limit(cap))).catch(() => ({ docs: [], size: 0 })),
  ]);

  const byId = new Map();

  mapDropDocs(openSnap).forEach((drop) => {
    byId.set(drop.id, drop);
  });

  mapDropDocs(allSnap).forEach((drop) => {
    if (byId.has(drop.id)) return;
    if (isOpenFeedDrop(drop)) {
      byId.set(drop.id, drop);
    }
  });

  return Array.from(byId.values()).slice(0, cap);
}

/**
 * Open drops from creators the fan follows (Home feed).
 */
export async function fetchFollowingFeedDrops(followingIds, max = FEED_DROP_LIMIT) {
  if (!followingIds?.length) return [];

  const followingSet = new Set(followingIds);
  const drops = await fetchFeedDrops(max);

  return drops
    .filter((d) => followingSet.has(d.creatorId))
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
}

/**
 * Platform-wide open drops sorted by fewest views remaining (Closing now rows).
 */
export async function fetchClosingSoonDrops(max = FEED_DROP_LIMIT) {
  const drops = await fetchFeedDrops(max);
  return drops
    .filter(isOpenFeedDrop)
    .map((d) => ({
      ...d,
      _left: Math.max((d.maxViews || 100) - (d.viewCount || 0), 0),
    }))
    .sort((a, b) => a._left - b._left);
}
